/*!
 * Snowplow media tracking v4.8.1 (https://github.com/snowplow/snowplow-javascript-tracker)
 * Copyright 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * Licensed under BSD-3-Clause
 */

import { __assign } from 'tslib';
import { dispatchToTrackersInCollection } from '@snowplow/browser-tracker-core';
import { LOG, resolveDynamicContext, buildSelfDescribingEvent } from '@snowplow/tracker-core';

/** Type of media player event */
var MediaEventType;
(function (MediaEventType) {
    // Controlling the playback
    /** Media player event fired when the media tracking is successfully attached to the player and can track events. */
    MediaEventType["Ready"] = "ready";
    /** Media player event sent when the player changes state to playing from previously being paused. */
    MediaEventType["Play"] = "play";
    /** Media player event sent when the user pauses the playback. */
    MediaEventType["Pause"] = "pause";
    /** Media player event sent when playback stops when end of the media is reached or because no further data is available. */
    MediaEventType["End"] = "end";
    /** Media player event sent when a seek operation begins. */
    MediaEventType["SeekStart"] = "seek_start";
    /** Media player event sent when a seek operation completes. */
    MediaEventType["SeekEnd"] = "seek_end";
    // Changes in playback settings
    /** Media player event sent when the playback rate has changed. */
    MediaEventType["PlaybackRateChange"] = "playback_rate_change";
    /** Media player event sent when the volume has changed. */
    MediaEventType["VolumeChange"] = "volume_change";
    /** Media player event fired immediately after the browser switches into or out of full-screen mode. */
    MediaEventType["FullscreenChange"] = "fullscreen_change";
    /** Media player event fired immediately after the browser switches into or out of picture-in-picture mode. */
    MediaEventType["PictureInPictureChange"] = "picture_in_picture_change";
    // Tracking playback progress
    /** Media player event fired periodically during main content playback, regardless of other API events that have been sent. */
    MediaEventType["Ping"] = "ping";
    /** Media player event fired when a percentage boundary set in options.boundaries is reached */
    MediaEventType["PercentProgress"] = "percent_progress";
    // Ad events
    /** Media player event that signals the start of an ad break. */
    MediaEventType["AdBreakStart"] = "ad_break_start";
    /** Media player event that signals the end of an ad break. */
    MediaEventType["AdBreakEnd"] = "ad_break_end";
    /** Media player event that signals the start of an ad. */
    MediaEventType["AdStart"] = "ad_start";
    /** Media player event fired when a quartile of ad is reached after continuous ad playback at normal speed. */
    MediaEventType["AdFirstQuartile"] = "ad_first_quartile";
    /** Media player event fired when a midpoint of ad is reached after continuous ad playback at normal speed. */
    MediaEventType["AdMidpoint"] = "ad_midpoint";
    /** Media player event fired when a quartile of ad is reached after continuous ad playback at normal speed. */
    MediaEventType["AdThirdQuartile"] = "ad_third_quartile";
    /** Media player event that signals the ad creative was played to the end at normal speed. */
    MediaEventType["AdComplete"] = "ad_complete";
    /** Media player event fired when the user activated a skip control to skip the ad creative. */
    MediaEventType["AdSkip"] = "ad_skip";
    /** Media player event fired when the user clicked on the ad. */
    MediaEventType["AdClick"] = "ad_click";
    /** Media player event fired when the user clicked the pause control and stopped the ad creative. */
    MediaEventType["AdPause"] = "ad_pause";
    /** Media player event fired when the user resumed playing the ad creative after it had been stopped or paused. */
    MediaEventType["AdResume"] = "ad_resume";
    // Data quality events
    /** Media player event fired when the player goes into the buffering state and begins to buffer content. */
    MediaEventType["BufferStart"] = "buffer_start";
    /** Media player event fired when the the player finishes buffering content and resumes playback. */
    MediaEventType["BufferEnd"] = "buffer_end";
    /** Media player event tracked when the video playback quality changes automatically. */
    MediaEventType["QualityChange"] = "quality_change";
    /** Media player event tracked when the resource could not be loaded due to an error.  */
    MediaEventType["Error"] = "error";
})(MediaEventType || (MediaEventType = {}));
/** Type of media content. */
var MediaType;
(function (MediaType) {
    /** Audio content. */
    MediaType["Audio"] = "audio";
    /** Video content. */
    MediaType["Video"] = "video";
})(MediaType || (MediaType = {}));
/** Type of ads within the break */
var MediaAdBreakType;
(function (MediaAdBreakType) {
    /** Take full control of the video for a period of time. */
    MediaAdBreakType["Linear"] = "linear";
    /** Run concurrently to the video. */
    MediaAdBreakType["NonLinear"] = "nonlinear";
    /** Accompany the video but placed outside the player. */
    MediaAdBreakType["Companion"] = "companion";
})(MediaAdBreakType || (MediaAdBreakType = {}));

var MEDIA_SCHEMA_PREFIX = 'iglu:com.snowplowanalytics.snowplow.media/';
var MEDIA_SCHEMA_SUFFIX = '/jsonschema/1-0-0';
// NOTE: The player schema has a different vendor than the other media schemas because it builds on an older version of the same schema. Versions 3.12 to 3.13.1 of the media plugin used a conflicting schema URI which has since been removed from Iglu Central.
var MEDIA_PLAYER_SCHEMA = 'iglu:com.snowplowanalytics.snowplow/media_player/jsonschema/2-0-0';
var MEDIA_SESSION_SCHEMA = MEDIA_SCHEMA_PREFIX + 'session' + MEDIA_SCHEMA_SUFFIX;
var MEDIA_AD_SCHEMA = MEDIA_SCHEMA_PREFIX + 'ad' + MEDIA_SCHEMA_SUFFIX;
var MEDIA_AD_BREAK_SCHEMA = MEDIA_SCHEMA_PREFIX + 'ad_break' + MEDIA_SCHEMA_SUFFIX;
function eventNameForEventType(eventType) {
    /// ad first quartile, midpoint, and third quartile events share the same schema
    switch (eventType) {
        case MediaEventType.AdFirstQuartile:
            return 'ad_quartile';
        case MediaEventType.AdMidpoint:
            return 'ad_quartile';
        case MediaEventType.AdThirdQuartile:
            return 'ad_quartile';
    }
    return eventType;
}
function getMediaEventSchema(eventType) {
    var eventName = eventNameForEventType(eventType);
    return MEDIA_SCHEMA_PREFIX + eventName + '_event' + MEDIA_SCHEMA_SUFFIX;
}

function buildMediaPlayerEvent(event) {
    var _a;
    return {
        schema: getMediaEventSchema(event.type),
        data: removeEmptyProperties((_a = event.eventBody) !== null && _a !== void 0 ? _a : {}),
    };
}
function buildMediaPlayerEntity(mediaPlayer) {
    return {
        schema: MEDIA_PLAYER_SCHEMA,
        data: removeEmptyProperties(mediaPlayer),
    };
}
function buildMediaSessionEntity(session) {
    return {
        schema: MEDIA_SESSION_SCHEMA,
        data: removeEmptyProperties(session),
    };
}
function buildMediaAdEntity(ad) {
    return {
        schema: MEDIA_AD_SCHEMA,
        data: removeEmptyProperties(ad),
    };
}
function buildMediaAdBreakEntity(adBreak) {
    return {
        schema: MEDIA_AD_BREAK_SCHEMA,
        data: removeEmptyProperties(adBreak),
    };
}
/**
 * Returns a copy of a JSON with undefined and null properties removed
 *
 * @param event - Object to clean
 * @returns A cleaned copy of eventJson
 */
function removeEmptyProperties(event) {
    var ret = {};
    for (var k in event) {
        if (event[k] != null) {
            ret[k] = event[k];
        }
    }
    return ret;
}

/** Keeps track of the ad and ad break entities and updates them according to tracked events. */
var MediaAdTracking = /** @class */ (function () {
    function MediaAdTracking() {
        this.podPosition = 0;
    }
    MediaAdTracking.prototype.updateForThisEvent = function (eventType, player, ad, adBreak) {
        if (eventType == MediaEventType.AdStart) {
            this.ad = undefined;
            this.podPosition++;
        }
        else if (eventType == MediaEventType.AdBreakStart) {
            this.adBreak = undefined;
            this.podPosition = 0;
        }
        if (ad !== undefined) {
            var position = { podPosition: this.podPosition > 0 ? this.podPosition : undefined };
            if (this.ad !== undefined) {
                this.ad = __assign(__assign(__assign({}, this.ad), position), ad);
            }
            else {
                this.ad = __assign(__assign({}, position), ad);
            }
        }
        if (adBreak !== undefined) {
            var startTime = { startTime: player.currentTime };
            if (this.adBreak !== undefined) {
                this.adBreak = __assign(__assign(__assign({}, startTime), this.adBreak), adBreak);
            }
            else {
                this.adBreak = __assign(__assign({}, startTime), adBreak);
            }
        }
    };
    MediaAdTracking.prototype.updateForNextEvent = function (eventType) {
        if (eventType == MediaEventType.AdBreakEnd) {
            this.adBreak = undefined;
            this.podPosition = 0;
        }
        if (eventType == MediaEventType.AdComplete || eventType == MediaEventType.AdSkip) {
            this.ad = undefined;
        }
    };
    MediaAdTracking.prototype.getContext = function () {
        var context = [];
        if (this.ad !== undefined) {
            context.push(buildMediaAdEntity(this.ad));
        }
        if (this.adBreak !== undefined) {
            context.push(buildMediaAdBreakEntity(this.adBreak));
        }
        return context;
    };
    return MediaAdTracking;
}());

/**
 * This class filters out repeated events that are sent by the media player.
 * This applies to seek and volume change events.
 */
var RepeatedEventFilter = /** @class */ (function () {
    function RepeatedEventFilter(configuration) {
        var _this = this;
        var _a;
        this.aggregateEventsWithOrder = {};
        this.eventsToAggregate = {};
        var allFiltersEnabled = configuration === undefined || configuration === true;
        if (allFiltersEnabled || (typeof configuration === 'object' && configuration.seekEvents !== false)) {
            this.aggregateEventsWithOrder[getMediaEventSchema(MediaEventType.SeekStart)] = true;
            this.aggregateEventsWithOrder[getMediaEventSchema(MediaEventType.SeekEnd)] = false;
        }
        if (allFiltersEnabled || (typeof configuration === 'object' && configuration.volumeChangeEvents !== false)) {
            this.aggregateEventsWithOrder[getMediaEventSchema(MediaEventType.VolumeChange)] = false;
        }
        this.flushTimeoutMs = (_a = (typeof configuration === 'object' ? configuration.flushTimeoutMs : undefined)) !== null && _a !== void 0 ? _a : 5000;
        Object.keys(this.aggregateEventsWithOrder).forEach(function (schema) {
            _this.eventsToAggregate[schema] = [];
        });
    }
    RepeatedEventFilter.prototype.trackFilteredEvents = function (events, trackEvent) {
        var _this = this;
        var startFlushTimeout = false;
        events.forEach(function (_a) {
            var event = _a.event, context = _a.context;
            if (_this.eventsToAggregate[event.schema] !== undefined) {
                startFlushTimeout = true;
                _this.eventsToAggregate[event.schema].push(function () { return trackEvent({ event: event, context: context }); });
            }
            else {
                startFlushTimeout = false;
                // flush any events waiting
                _this.flush();
                trackEvent({ event: event, context: context });
            }
        });
        if (startFlushTimeout && this.flushTimeout === undefined) {
            this.setFlushTimeout();
        }
    };
    RepeatedEventFilter.prototype.flush = function () {
        var _this = this;
        this.clearFlushTimeout();
        Object.keys(this.eventsToAggregate).forEach(function (schema) {
            var eventsToAggregate = _this.eventsToAggregate[schema];
            if (eventsToAggregate.length > 0) {
                if (_this.aggregateEventsWithOrder[schema]) {
                    eventsToAggregate[0]();
                }
                else {
                    eventsToAggregate[eventsToAggregate.length - 1]();
                }
                _this.eventsToAggregate[schema] = [];
            }
        });
    };
    RepeatedEventFilter.prototype.clearFlushTimeout = function () {
        if (this.flushTimeout !== undefined) {
            clearTimeout(this.flushTimeout);
            this.flushTimeout = undefined;
        }
    };
    RepeatedEventFilter.prototype.setFlushTimeout = function () {
        var _this = this;
        this.clearFlushTimeout();
        this.flushTimeout = window.setTimeout(function () { return _this.flush(); }, this.flushTimeoutMs);
    };
    return RepeatedEventFilter;
}());

/**
 * Manages the state and built-in entities for a media tracking that starts when a user
 * call `startMediaTracking` and ends with `endMediaTracking`.
 *
 * It updates the internal state for each tracked event and returns context entities with
 * properties updated based on the internal state.
 */
var MediaTracking = /** @class */ (function () {
    function MediaTracking(id, player, session, pingInterval, boundaries, captureEvents, updatePageActivityWhilePlaying, filterRepeatedEvents, context) {
        /// List of boundaries for which percent progress events were already sent to avoid sending again.
        this.sentBoundaries = [];
        /// State for the media player context entity that is updated as new events are tracked.
        this.player = {
            currentTime: 0,
            paused: true,
            ended: false,
        };
        /// Manages ad entities.
        this.adTracking = new MediaAdTracking();
        this.id = id;
        this.updatePlayer(player);
        this.session = session;
        this.pingInterval = pingInterval;
        this.boundaries = boundaries;
        this.captureEvents = captureEvents;
        this.updatePageActivityWhilePlaying = updatePageActivityWhilePlaying;
        this.customContext = context;
        this.repeatedEventFilter = new RepeatedEventFilter(filterRepeatedEvents);
        // validate event names in the captureEvents list
        captureEvents === null || captureEvents === void 0 ? void 0 : captureEvents.forEach(function (eventType) {
            if (!Object.values(MediaEventType).includes(eventType)) {
                LOG.warn('Unknown media event ' + eventType);
            }
        });
    }
    /**
     * Called when user calls `endMediaTracking()`.
     */
    MediaTracking.prototype.flushAndStop = function () {
        var _a;
        (_a = this.pingInterval) === null || _a === void 0 ? void 0 : _a.clear();
        this.repeatedEventFilter.flush();
    };
    /**
     * Updates the internal state given the new event or new media player info and returns events to track.
     * @param eventType Type of the event tracked or undefined when only updating player properties.
     * @param player Updates to the media player stored entity.
     * @param ad Updates to the ad entity.
     * @param adBreak Updates to the ad break entity.
     * @returns List of events with entities to track.
     */
    MediaTracking.prototype.update = function (trackEvent, mediaEvent, customEvent, player, ad, adBreak) {
        var _a, _b;
        // update state
        this.updatePlayer(player);
        if (mediaEvent !== undefined) {
            this.adTracking.updateForThisEvent(mediaEvent.type, this.player, ad, adBreak);
        }
        (_a = this.session) === null || _a === void 0 ? void 0 : _a.update(mediaEvent === null || mediaEvent === void 0 ? void 0 : mediaEvent.type, this.player, this.adTracking.adBreak);
        (_b = this.pingInterval) === null || _b === void 0 ? void 0 : _b.update(this.player);
        // build context entities
        var context = [buildMediaPlayerEntity(this.player)];
        if (this.session !== undefined) {
            context.push(this.session.getContext());
        }
        if (this.customContext) {
            context = context.concat(resolveDynamicContext(this.customContext, mediaEvent !== null && mediaEvent !== void 0 ? mediaEvent : customEvent));
        }
        context = context.concat(this.adTracking.getContext());
        // build event types to track
        var mediaEventsToTrack = [];
        if (mediaEvent !== undefined && this.shouldTrackEvent(mediaEvent.type)) {
            mediaEventsToTrack.push(mediaEvent);
        }
        if (this.shouldSendPercentProgress()) {
            mediaEventsToTrack.push({
                type: MediaEventType.PercentProgress,
                eventBody: { percentProgress: this.getPercentProgress() },
            });
        }
        // update state for events after this one
        if (mediaEvent !== undefined) {
            this.adTracking.updateForNextEvent(mediaEvent.type);
        }
        var eventsToTrack = mediaEventsToTrack.map(function (event) {
            return { event: buildMediaPlayerEvent(event), context: context };
        });
        if (customEvent !== undefined) {
            eventsToTrack.push({ event: customEvent, context: context });
        }
        this.repeatedEventFilter.trackFilteredEvents(eventsToTrack, trackEvent);
    };
    MediaTracking.prototype.shouldUpdatePageActivity = function () {
        var _a;
        return ((_a = this.updatePageActivityWhilePlaying) !== null && _a !== void 0 ? _a : true) && !this.player.paused;
    };
    MediaTracking.prototype.updatePlayer = function (player) {
        if (player !== undefined) {
            this.player = __assign(__assign({}, this.player), player);
        }
    };
    MediaTracking.prototype.shouldSendPercentProgress = function () {
        var percentProgress = this.getPercentProgress();
        if (this.boundaries === undefined || percentProgress === undefined || this.player.paused) {
            return false;
        }
        var boundaries = this.boundaries.filter(function (b) { return b <= (percentProgress !== null && percentProgress !== void 0 ? percentProgress : 0); });
        if (boundaries.length == 0) {
            return false;
        }
        var boundary = Math.max.apply(Math, boundaries);
        if (!this.sentBoundaries.includes(boundary)) {
            this.sentBoundaries.push(boundary);
            return true;
        }
        return false;
    };
    MediaTracking.prototype.shouldTrackEvent = function (eventType) {
        return this.captureEvents === undefined || this.captureEvents.includes(eventType);
    };
    MediaTracking.prototype.getPercentProgress = function () {
        var _a;
        if (this.player.duration === null || this.player.duration === undefined || this.player.duration == 0) {
            return undefined;
        }
        return Math.floor((((_a = this.player.currentTime) !== null && _a !== void 0 ? _a : 0) / this.player.duration) * 100);
    };
    return MediaTracking;
}());

/**
 * Default ping interval in seconds.
 * Changing the setting is a breaking change for downstream models that work with this setting.
 * If a custom interval is used, it is present in the session context entity.
 **/
var DEFAULT_PING_INTERVAL = 30;
/** Manages the timer for firing the media ping events. */
var MediaPingInterval = /** @class */ (function () {
    function MediaPingInterval(pingIntervalSeconds, maxPausedPings, trackPing) {
        var _this = this;
        this.numPausedPings = 0;
        this.maxPausedPings = 1;
        if (maxPausedPings !== undefined) {
            this.maxPausedPings = maxPausedPings;
        }
        this.interval = setInterval(function () {
            if (!_this.isPaused() || _this.numPausedPings < _this.maxPausedPings) {
                if (_this.isPaused()) {
                    _this.numPausedPings++;
                }
                trackPing();
            }
        }, (pingIntervalSeconds !== null && pingIntervalSeconds !== void 0 ? pingIntervalSeconds : DEFAULT_PING_INTERVAL) * 1000);
    }
    MediaPingInterval.prototype.update = function (player) {
        this.paused = player.paused;
        if (!this.paused) {
            this.numPausedPings = 0;
        }
    };
    MediaPingInterval.prototype.clear = function () {
        if (this.interval !== undefined) {
            clearInterval(this.interval);
            this.interval = undefined;
        }
    };
    MediaPingInterval.prototype.isPaused = function () {
        return this.paused === true;
    };
    return MediaPingInterval;
}());

/** Maximum content-time gap (seconds) for which intermediate seconds are filled into playedSeconds.
 *  Beyond this threshold only the current second is recorded, preventing unbounded iteration
 *  for live streams or large VOD seeks where currentTime can be a huge offset.
 */
var MAX_CONTENT_TIME_STEP = 3600;
var adStartTypes = [MediaEventType.AdStart, MediaEventType.AdResume];
var adProgressTypes = [
    MediaEventType.AdClick,
    MediaEventType.AdFirstQuartile,
    MediaEventType.AdMidpoint,
    MediaEventType.AdThirdQuartile,
];
var adEndTypes = [MediaEventType.AdComplete, MediaEventType.AdSkip, MediaEventType.AdPause];
var bufferingEndTypes = [MediaEventType.BufferEnd, MediaEventType.Play];
/** Calculates the average playback rate based on measurements of the rate over partial durations */
var AveragePlaybackRateCalculator = /** @class */ (function () {
    function AveragePlaybackRateCalculator() {
        this.durationWithPlaybackRate = 0;
        this.duration = 0;
    }
    AveragePlaybackRateCalculator.prototype.add = function (rate, duration) {
        this.durationWithPlaybackRate += rate * duration;
        this.duration += duration;
    };
    AveragePlaybackRateCalculator.prototype.get = function () {
        return this.duration > 0 ? this.durationWithPlaybackRate / this.duration : undefined;
    };
    return AveragePlaybackRateCalculator;
}());
/**
 * Calculates statistics in the media player session as events are tracked.
 */
var MediaSessionTrackingStats = /** @class */ (function () {
    function MediaSessionTrackingStats() {
        /// time for which ads were playing
        this.adPlaybackDuration = 0;
        /// time for which the content was playing
        this.playbackDuration = 0;
        /// time for which the content was playing on mute
        this.playbackDurationMuted = 0;
        /// average playback rate calculator
        this.avgPlaybackRate = new AveragePlaybackRateCalculator();
        /// time for which the playback was paused
        this.pausedDuration = 0;
        /// number of ads
        this.ads = 0;
        /// number of ad breaks
        this.adBreaks = 0;
        /// number of ad skip events
        this.adsSkipped = 0;
        /// number of ad click events
        this.adsClicked = 0;
        /// sum of time durations between the buffer start event and end of buffering
        this.bufferingDuration = 0;
        /// set of seconds in content time that were played used to calculate the content watched duration
        this.playedSeconds = new Set();
    }
    /// Update stats given a new event.
    MediaSessionTrackingStats.prototype.update = function (eventType, player, adBreak) {
        var _a;
        var log = {
            time: new Date().getTime() / 1000,
            contentTime: player.currentTime,
            eventType: eventType,
            playbackRate: player.playbackRate,
            paused: player.paused,
            muted: player.muted,
            linearAd: ((_a = adBreak === null || adBreak === void 0 ? void 0 : adBreak.breakType) !== null && _a !== void 0 ? _a : MediaAdBreakType.Linear) == MediaAdBreakType.Linear,
        };
        this.updateDurationStats(log);
        this.updateAdStats(log);
        this.updateBufferingStats(log);
        this.lastLog = log;
    };
    /// Produce part of the media session entity with the stats.
    MediaSessionTrackingStats.prototype.toSessionContextEntity = function () {
        return {
            timePaused: this.pausedDuration > 0 ? this.round(this.pausedDuration) : undefined,
            timePlayed: this.playbackDuration > 0 ? this.round(this.playbackDuration) : undefined,
            timePlayedMuted: this.playbackDurationMuted > 0 ? this.round(this.playbackDurationMuted) : undefined,
            timeSpentAds: this.adPlaybackDuration > 0 ? this.round(this.adPlaybackDuration) : undefined,
            timeBuffering: this.bufferingDuration > 0 ? this.round(this.bufferingDuration) : undefined,
            ads: this.ads > 0 ? this.ads : undefined,
            adBreaks: this.adBreaks > 0 ? this.adBreaks : undefined,
            adsSkipped: this.adsSkipped > 0 ? this.adsSkipped : undefined,
            adsClicked: this.adsClicked > 0 ? this.adsClicked : undefined,
            avgPlaybackRate: this.round(this.avgPlaybackRate.get()),
            contentWatched: this.playedSeconds.size > 0 ? this.playedSeconds.size : undefined,
        };
    };
    MediaSessionTrackingStats.prototype.updateDurationStats = function (log) {
        var _a;
        // if ad was playing until now and it was a linear ad, don't add the duration stats
        var wasPlayingAd = this.lastAdUpdateAt !== undefined;
        var shouldCountStats = (_a = (!wasPlayingAd || !log.linearAd)) !== null && _a !== void 0 ? _a : true;
        if (!shouldCountStats) {
            return;
        }
        if (this.lastLog !== undefined) {
            // add the time diff since last event to duration stats
            var duration = log.time - this.lastLog.time;
            if (this.lastLog.paused) {
                this.pausedDuration += duration;
            }
            else {
                this.playbackDuration += duration;
                if (this.lastLog.playbackRate !== undefined) {
                    this.avgPlaybackRate.add(this.lastLog.playbackRate, duration);
                }
                if (this.lastLog.muted) {
                    this.playbackDurationMuted += duration;
                }
                if (!log.paused) {
                    var gap = log.contentTime - this.lastLog.contentTime;
                    if (gap <= MAX_CONTENT_TIME_STEP) {
                        for (var i = Math.floor(this.lastLog.contentTime); i < log.contentTime; i++) {
                            this.playedSeconds.add(i);
                        }
                    }
                    // gap > MAX_CONTENT_TIME_STEP: skip loop, only current second is recorded below
                }
            }
        }
        if (!log.paused) {
            this.playedSeconds.add(Math.floor(log.contentTime));
        }
    };
    MediaSessionTrackingStats.prototype.updateAdStats = function (log) {
        // only works with ad event types
        if (log.eventType === undefined) {
            return;
        }
        // count ad actions
        if (log.eventType == MediaEventType.AdBreakStart) {
            this.adBreaks++;
        }
        else if (log.eventType == MediaEventType.AdStart) {
            this.ads++;
        }
        else if (log.eventType == MediaEventType.AdSkip) {
            this.adsSkipped++;
        }
        else if (log.eventType == MediaEventType.AdClick) {
            this.adsClicked++;
        }
        // update ad playback duration
        if (this.lastAdUpdateAt === undefined) {
            if (adStartTypes.includes(log.eventType)) {
                this.lastAdUpdateAt = log.time;
            }
        }
        else if (adProgressTypes.includes(log.eventType)) {
            this.adPlaybackDuration += log.time - this.lastAdUpdateAt;
            this.lastAdUpdateAt = log.time;
        }
        else if (adEndTypes.includes(log.eventType)) {
            this.adPlaybackDuration += log.time - this.lastAdUpdateAt;
            this.lastAdUpdateAt = undefined;
        }
    };
    MediaSessionTrackingStats.prototype.updateBufferingStats = function (log) {
        if (log.eventType == MediaEventType.BufferStart) {
            this.bufferingStartedAt = log.time;
            this.bufferingStartTime = log.contentTime;
        }
        else if (this.bufferingStartedAt !== undefined) {
            // Either the playback moved or BufferEnd or Play events were tracked
            if ((log.contentTime != this.bufferingStartTime && !log.paused) ||
                (log.eventType !== undefined && bufferingEndTypes.includes(log.eventType))) {
                this.bufferingDuration += log.time - this.bufferingStartedAt;
                this.bufferingStartedAt = undefined;
                this.bufferingStartTime = undefined;
            }
            else {
                this.bufferingDuration += log.time - this.bufferingStartedAt;
                this.bufferingStartedAt = log.time;
            }
        }
    };
    MediaSessionTrackingStats.prototype.round = function (n) {
        if (n === undefined) {
            return undefined;
        }
        return Math.round(n * 1000) / 1000;
    };
    return MediaSessionTrackingStats;
}());

/**
 * Manages the media player session that is optionally added as a context entity
 * in media tracking.
 */
var MediaSessionTracking = /** @class */ (function () {
    function MediaSessionTracking(id, startedAt, pingInterval) {
        this.stats = new MediaSessionTrackingStats();
        this.id = id;
        this.pingInterval = pingInterval;
        this.startedAt = startedAt !== null && startedAt !== void 0 ? startedAt : new Date();
    }
    MediaSessionTracking.prototype.update = function (eventType, player, adBreak) {
        this.stats.update(eventType, player, adBreak);
    };
    MediaSessionTracking.prototype.getContext = function () {
        return buildMediaSessionEntity(__assign({ mediaSessionId: this.id, startedAt: this.startedAt, pingInterval: this.pingInterval }, this.stats.toSessionContextEntity()));
    };
    return MediaSessionTracking;
}());

var _trackers = {};
var _context = {};
/**
 * Adds media tracking
 */
function SnowplowMediaPlugin() {
    var trackerId;
    return {
        activateBrowserPlugin: function (tracker) {
            trackerId = tracker.id;
            _trackers[trackerId] = tracker;
            _context[trackerId] = [];
        },
        contexts: function () {
            return _context[trackerId] || [];
        },
    };
}
var activeMedias = {};
/**
 * Starts media tracking for a single media content tracked in a media player.
 * The tracking instance is uniquely identified by a given ID.
 * All subsequent media track calls will be processed within this media tracking if given the same ID.
 *
 * @param config Configuration for setting up media tracking
 * @param trackers The tracker identifiers which ping events will be sent to
 */
function startMediaTracking(config, trackers) {
    var _a, _b, _c;
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var pingInterval = typeof config.pings === 'boolean' ? undefined : (_a = config.pings) === null || _a === void 0 ? void 0 : _a.pingInterval;
    var maxPausedPings = typeof config.pings === 'boolean' ? undefined : (_b = config.pings) === null || _b === void 0 ? void 0 : _b.maxPausedPings;
    var pings = config.pings === false || config.pings === undefined
        ? undefined
        : new MediaPingInterval(pingInterval, maxPausedPings, function () {
            track({ mediaEvent: { type: MediaEventType.Ping } }, { id: config.id }, trackers);
        });
    var sessionTracking = config.session === false ? undefined : new MediaSessionTracking(config.id, (_c = config.session) === null || _c === void 0 ? void 0 : _c.startedAt, pingInterval);
    var mediaTracking = new MediaTracking(config.id, config.player, sessionTracking, pings, config.boundaries, config.captureEvents, config.updatePageActivityWhilePlaying, config.filterOutRepeatedEvents, config.context);
    activeMedias[mediaTracking.id] = mediaTracking;
}
/**
 * Ends media tracking with the given ID if previously started.
 * Clears local state for the media tracking and sends any events waiting to be sent.
 *
 * @param configuration Configuration with the media tracking ID
 */
function endMediaTracking(configuration) {
    if (activeMedias[configuration.id]) {
        activeMedias[configuration.id].flushAndStop();
        delete activeMedias[configuration.id];
    }
}
/**
 * Updates stored attributes of the media tracking such as the current playback.
 * Use this function to continually update the player attributes so that they
 * can be sent in the background ping events.
 *
 * @param args The attributes for the media entities
 * @param trackers The tracker identifiers which any resulting event will be sent to
 */
function updateMediaTracking(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({}, args, trackers);
}
/**
 * Tracks a media player ready event that is fired when the media tracking is successfully
 * attached to the player and can track events.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaReady(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.Ready } }, args, trackers);
}
/**
 * Tracks a media player play event sent when the player changes state to playing from
 * previously being paused.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaPlay(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.Play } }, __assign(__assign({}, args), { player: __assign(__assign({}, args.player), { paused: false }) }), trackers);
}
/**
 * Tracks a media player pause event sent when the user pauses the playback.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaPause(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.Pause } }, __assign(__assign({}, args), { player: __assign(__assign({}, args.player), { paused: true }) }), trackers);
}
/**
 * Tracks a media player end event sent when playback stops when end of the media
 * is reached or because no further data is available.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaEnd(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.End } }, __assign(__assign({}, args), { player: __assign(__assign({}, args.player), { ended: true, paused: true }) }), trackers);
}
/**
 * Tracks a media player seek start event sent when a seek operation begins.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaSeekStart(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.SeekStart } }, args, trackers);
}
/**
 * Tracks a media player seek end event sent when a seek operation completes.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaSeekEnd(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.SeekEnd } }, args, trackers);
}
/**
 * Tracks a media player playback rate change event sent when the playback rate has changed.
 *
 * If not passed here, the previous rate is taken from the last setting in media player.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaPlaybackRateChange(args, trackers) {
    var _a;
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var previousRate = args.previousRate, newRate = args.newRate;
    track({
        mediaEvent: {
            type: MediaEventType.PlaybackRateChange,
            eventBody: {
                previousRate: previousRate !== null && previousRate !== void 0 ? previousRate : (_a = getMedia(args.id)) === null || _a === void 0 ? void 0 : _a.player.playbackRate,
                newRate: newRate,
            },
        },
    }, __assign(__assign({}, args), { player: __assign(__assign({}, args.player), { playbackRate: newRate }) }), trackers);
}
/**
 * Tracks a media player volume change event sent when the volume has changed.
 *
 * If not passed here, the previous volume is taken from the last setting in media player.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaVolumeChange(args, trackers) {
    var _a;
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var previousVolume = args.previousVolume, newVolume = args.newVolume;
    track({
        mediaEvent: {
            type: MediaEventType.VolumeChange,
            eventBody: {
                previousVolume: previousVolume !== null && previousVolume !== void 0 ? previousVolume : (_a = getMedia(args.id)) === null || _a === void 0 ? void 0 : _a.player.volume,
                newVolume: newVolume,
            },
        },
    }, __assign(__assign({}, args), { player: __assign(__assign({}, args.player), { volume: newVolume }) }), trackers);
}
/**
 * Tracks a media player fullscreen change event fired immediately after
 * the browser switches into or out of full-screen mode.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaFullscreenChange(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var fullscreen = args.fullscreen;
    track({
        mediaEvent: {
            type: MediaEventType.FullscreenChange,
            eventBody: { fullscreen: fullscreen },
        },
    }, __assign(__assign({}, args), { player: __assign(__assign({}, args.player), { fullscreen: fullscreen }) }), trackers);
}
/**
 * Tracks a media player picture-in-picture change event fired immediately
 * after the browser switches into or out of picture-in-picture mode.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaPictureInPictureChange(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var pictureInPicture = args.pictureInPicture;
    track({
        mediaEvent: {
            type: MediaEventType.PictureInPictureChange,
            eventBody: { pictureInPicture: pictureInPicture },
        },
    }, __assign(__assign({}, args), { player: __assign(__assign({}, args.player), { pictureInPicture: pictureInPicture }) }), trackers);
}
/**
 * Tracks a media player ad break start event that signals the start of an ad break.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdBreakStart(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.AdBreakStart } }, args, trackers);
}
/**
 * Tracks a media player ad break end event that signals the end of an ad break.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdBreakEnd(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.AdBreakEnd } }, args, trackers);
}
/**
 * Tracks a media player ad start event that signals the start of an ad.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdStart(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.AdStart } }, args, trackers);
}
/**
 * Tracks a media player ad skip event fired when the user activated
 * a skip control to skip the ad creative.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdSkip(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var percentProgress = args.percentProgress;
    if (percentProgress !== undefined) {
        percentProgress = Math.floor(percentProgress);
    }
    track({
        mediaEvent: {
            type: MediaEventType.AdSkip,
            eventBody: { percentProgress: percentProgress },
        },
    }, args, trackers);
}
/**
 * Tracks a media player ad first quartile played event fired when
 * a quartile of ad is reached after continuous ad playback at normal speed.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdFirstQuartile(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({
        mediaEvent: {
            type: MediaEventType.AdFirstQuartile,
            eventBody: { percentProgress: 25 },
        },
    }, args, trackers);
}
/**
 * Tracks a media player ad midpoint played event fired when a midpoint of ad is
 * reached after continuous ad playback at normal speed.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdMidpoint(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({
        mediaEvent: {
            type: MediaEventType.AdMidpoint,
            eventBody: { percentProgress: 50 },
        },
    }, args, trackers);
}
/**
 * Tracks media player ad third quartile played event fired when a quartile
 * of ad is reached after continuous ad playback at normal speed.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdThirdQuartile(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({
        mediaEvent: {
            type: MediaEventType.AdThirdQuartile,
            eventBody: { percentProgress: 75 },
        },
    }, args, trackers);
}
/**
 * Tracks a media player ad complete event that signals the ad creative
 * was played to the end at normal speed.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdComplete(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.AdComplete } }, args, trackers);
}
/**
 * Tracks a media player ad click event fired when the user clicked on the ad.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdClick(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var percentProgress = args.percentProgress;
    if (percentProgress !== undefined) {
        percentProgress = Math.floor(percentProgress);
    }
    track({
        mediaEvent: {
            type: MediaEventType.AdClick,
            eventBody: { percentProgress: percentProgress },
        },
    }, args, trackers);
}
/**
 * Tracks a media player ad pause event fired when the user clicked the pause
 * control and stopped the ad creative.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdPause(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var percentProgress = args.percentProgress;
    if (percentProgress !== undefined) {
        percentProgress = Math.floor(percentProgress);
    }
    track({
        mediaEvent: {
            type: MediaEventType.AdPause,
            eventBody: { percentProgress: percentProgress },
        },
    }, args, trackers);
}
/**
 * Tracks a media player ad resume event fired when the user resumed playing the
 * ad creative after it had been stopped or paused.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaAdResume(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var percentProgress = args.percentProgress;
    if (percentProgress !== undefined) {
        percentProgress = Math.floor(percentProgress);
    }
    track({
        mediaEvent: {
            type: MediaEventType.AdResume,
            eventBody: { percentProgress: percentProgress },
        },
    }, args, trackers);
}
/**
 * Tracks a media player buffering start event fired when the player goes
 * into the buffering state and begins to buffer content.
 *
 * End of buffering can be tracked using `trackMediaBufferEnd` or `trackMediaPlay` or
 * by updating the `currentTime` property.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaBufferStart(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.BufferStart } }, args, trackers);
}
/**
 * Tracks a media player buffering end event fired when the the player
 * finishes buffering content and resumes playback.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaBufferEnd(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    track({ mediaEvent: { type: MediaEventType.BufferEnd } }, args, trackers);
}
/**
 * Tracks a media player quality change event tracked when the video
 * playback quality changes automatically.
 *
 * If not passed here, the previous quality is taken from the last setting in media player.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaQualityChange(args, trackers) {
    var _a;
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var previousQuality = args.previousQuality, newQuality = args.newQuality, bitrate = args.bitrate, framesPerSecond = args.framesPerSecond, automatic = args.automatic, id = args.id;
    track({
        mediaEvent: {
            type: MediaEventType.QualityChange,
            eventBody: {
                previousQuality: previousQuality !== null && previousQuality !== void 0 ? previousQuality : (_a = getMedia(id)) === null || _a === void 0 ? void 0 : _a.player.quality,
                newQuality: newQuality,
                bitrate: bitrate,
                framesPerSecond: framesPerSecond,
                automatic: automatic,
            },
        },
    }, __assign(__assign({}, args), { player: __assign(__assign({}, args.player), { quality: newQuality }) }), trackers);
}
/**
 * Tracks a media player error event tracked when the resource could not be loaded due to an error.
 *
 * @param args The attributes for the media player event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaError(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var errorCode = args.errorCode, errorName = args.errorName, errorDescription = args.errorDescription;
    track({
        mediaEvent: {
            type: MediaEventType.Error,
            eventBody: { errorCode: errorCode, errorName: errorName, errorDescription: errorDescription },
        },
    }, args, trackers);
}
/**
 * Tracks a custom self-describing event within the context of the media tracking.
 * It will attach the context entities managed by the media tracking to the event (e.g. player, session, ad).
 *
 * @param args The attributes for the event and entities
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackMediaSelfDescribingEvent(args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var event = args.event;
    track({ customEvent: event }, args, trackers);
}
function getMedia(id) {
    if (activeMedias[id] === undefined) {
        LOG.error("Media tracking ".concat(id, " not started."));
        return undefined;
    }
    return activeMedias[id];
}
function track(event, args, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = args.context, context = _a === void 0 ? [] : _a, timestamp = args.timestamp, player = args.player, ad = args.ad, adBreak = args.adBreak, id = args.id;
    var mediaEvent = event.mediaEvent, customEvent = event.customEvent;
    var mediaTracking = getMedia(id);
    if (mediaTracking === undefined) {
        return;
    }
    var trackEvent = function (event) {
        dispatchToTrackersInCollection(trackers, _trackers, function (t) {
            var _a;
            t.core.track(buildSelfDescribingEvent(event), ((_a = event.context) !== null && _a !== void 0 ? _a : []).concat(resolveDynamicContext(context)), timestamp);
        });
    };
    mediaTracking.update(trackEvent, mediaEvent, customEvent, player, ad, adBreak);
    // Update page activity in order to keep sending page pings if needed
    if (mediaTracking.shouldUpdatePageActivity()) {
        dispatchToTrackersInCollection(trackers, _trackers, function (t) {
            t.updatePageActivity();
        });
    }
}

export { MediaEventType, MediaAdBreakType as MediaPlayerAdBreakType, SnowplowMediaPlugin, endMediaTracking, startMediaTracking, trackMediaAdBreakEnd, trackMediaAdBreakStart, trackMediaAdClick, trackMediaAdComplete, trackMediaAdFirstQuartile, trackMediaAdMidpoint, trackMediaAdPause, trackMediaAdResume, trackMediaAdSkip, trackMediaAdStart, trackMediaAdThirdQuartile, trackMediaBufferEnd, trackMediaBufferStart, trackMediaEnd, trackMediaError, trackMediaFullscreenChange, trackMediaPause, trackMediaPictureInPictureChange, trackMediaPlay, trackMediaPlaybackRateChange, trackMediaQualityChange, trackMediaReady, trackMediaSeekEnd, trackMediaSeekStart, trackMediaSelfDescribingEvent, trackMediaVolumeChange, updateMediaTracking };
//# sourceMappingURL=index.module.js.map
